import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTenant } from '../context/TenantContext';
import { loadGTM, unloadGTM } from '../utils/cookieManager';

const AnalyticsManager = () => {
  const { tenant } = useTenant();
  const location = useLocation();

  // 1. Handle GTM injection and consent changes
  useEffect(() => {
    const loadAnalytics = () => {
      // Check if user has accepted analytics cookies
      const consent = window.__cookieConsent;
      const gtmId = tenant?.tenantmasterconfig?.analytics?.gtmId;
      const gtmContainerId = tenant?.tenantmasterconfig?.analytics?.gtmContainerId;

      // CHANGE: Opt-out by default logic
      // Load if:
      // 1. Consent is not yet set (first visit)
      // 2. Consent is explicitly true
      const shouldLoad = !consent || consent.analytics === true;

      if (!shouldLoad) {
        // console.log('AnalyticsManager - Analytics explicitly rejected, skipping GTM load');
        return;
      }

      // Inject GA4 / gtag.js (G-XXXXXXX)
      if (gtmId) {
        loadGTM(gtmId);
      }

      // Inject GTM container (GTM-XXXXXXX) for conversion tracking
      if (gtmContainerId) {
        loadGTM(gtmContainerId);
      }
    };

    loadAnalytics();

    const handleConsentUpdate = (event) => {
      const gtmId = tenant?.tenantmasterconfig?.analytics?.gtmId;
      const gtmContainerId = tenant?.tenantmasterconfig?.analytics?.gtmContainerId;

      if (event.detail.analytics === false) {
        // User explicitly disabled analytics - unload and clear cookies
        window.__cookieConsent = { ...window.__cookieConsent, analytics: false };
        if (gtmId) unloadGTM(gtmId);
        if (gtmContainerId) unloadGTM(gtmContainerId);
      } else if (event.detail.analytics === true) {
        // User enabled analytics - load
        window.__cookieConsent = { ...window.__cookieConsent, analytics: true };
        if (gtmId) loadGTM(gtmId);
        if (gtmContainerId) loadGTM(gtmContainerId);
      }
    };

    window.addEventListener('cookieConsentUpdated', handleConsentUpdate);
    return () => window.removeEventListener('cookieConsentUpdated', handleConsentUpdate);
  }, [tenant]);

  // 2. Handle Virtual Page Views and Tenant Metadata on Route Changes
  useEffect(() => {
    const analyticsId = tenant?.tenantmasterconfig?.analytics?.gtmId;
    const consent = window.__cookieConsent;

    // Only track if analytics is configured AND (not yet consented OR explicitly accepted)
    const isAllowed = analyticsId && (!consent || consent.analytics === true);

    if (!isAllowed) return;

    const isGA4 = analyticsId && analyticsId.startsWith('G-');

    if (isGA4 && window.gtag) {
      // GA4 measurement ID — send page view via gtag
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_title: document.title,
        tenant_id: tenant?.tenantid,
        tenant_name: tenant?.tenantname,
        tenant_url: tenant?.tenanturlname,
      });
    } else if (!isGA4 && window.dataLayer) {
      // GTM container — push to dataLayer
      window.dataLayer.push({
        event: 'virtual_page_view',
        page_path: location.pathname + location.search,
        page_title: document.title,
        tenant_id: tenant?.tenantid,
        tenant_name: tenant?.tenantname,
        tenant_url: tenant?.tenanturlname,
        program_id: tenant?.tenanturlname
      });
    }
  }, [location, tenant]);

  return null; // This component doesn't render anything
};

export default AnalyticsManager;
